import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Building2,
  CreditCard,
  Link2,
  Loader2,
  Lock,
  Wallet,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import CheckoutShell, { StatusMessage } from '../components/checkout/CheckoutShell';
import {
  capturePaypalCheckout,
  fetchCheckoutIntent,
  payCheckoutIntent,
  resetCheckoutIntent,
  type CheckoutIntent,
  type CheckoutMethod,
} from '../lib/api';

const METHOD_ICON_SRC: Record<string, string> = {
  monero: '/icons/monero.svg',
  btcpay: '/icons/monero.svg',
};

const METHOD_DISPLAY: Partial<Record<string, { label: string; description: string }>> = {
  stripe: {
    label: 'Credit / debit card',
    description: 'Visa, Mastercard, Amex — secure checkout',
  },
  monero: {
    label: 'Monero — direct address',
    description: 'Unique subaddress from our wallet',
  },
  btcpay: {
    label: 'BTCPay',
    description: 'Pay with Bitcoin or Monero',
  },
};

const METHOD_ICONS: Record<string, typeof CreditCard> = {
  stripe: CreditCard,
  paypal: Wallet,
  mercury: Building2,
  paymento: Wallet,
  chain2pay: CreditCard,
  trocador: Lock,
};

function formatExpiryCountdown(expiresAt: string, nowMs: number): string {
  const end = new Date(expiresAt).getTime();
  const diff = end - nowMs;
  if (diff <= 0) return 'Expired';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) return `Link expires in ${days}d ${hours}h`;
  if (hours > 0) return `Link expires in ${hours}h ${minutes}m`;
  return `Link expires in ${minutes}m`;
}

function isPastExpiry(expiresAt: string, nowMs: number): boolean {
  return new Date(expiresAt).getTime() <= nowMs;
}

// Statuses where the intent can no longer be paid — show a clean terminal
// screen instead of the method list so the SPA never POSTs pay/{method} and
// gets a 410.
const TERMINAL_STATUS_COPY: Record<string, { title: string; description: string }> = {
  paid: {
    title: 'Payment paid',
    description: 'This checkout link is no longer available.',
  },
  expired: {
    title: 'Payment expired',
    description: 'This payment link has expired. Request a new link from your invoice.',
  },
  cancelled: {
    title: 'Payment cancelled',
    description: 'This payment link was cancelled. Request a new link from your invoice.',
  },
  failed: {
    title: 'Payment failed',
    description: 'This payment could not be completed. Request a new link from your invoice.',
  },
};

function MethodIcon({ methodId, className }: { methodId: string; className?: string }) {
  const src = METHOD_ICON_SRC[methodId];
  if (src) {
    return <img src={src} alt="" className={className} />;
  }
  const Icon = METHOD_ICONS[methodId] ?? Wallet;
  return <Icon className={className} />;
}

export default function Checkout() {
  const { token: routeToken } = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = routeToken ?? searchParams.get('token') ?? '';

  const [intent, setIntent] = useState<CheckoutIntent | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const status = searchParams.get('status');

  const expiredByTime = Boolean(intent?.expires_at && isPastExpiry(intent.expires_at, nowMs));
  const showExpired =
    intent?.payment_status === 'expired' || expiredByTime;

  useEffect(() => {
    if (!intent?.expires_at || showExpired) return;
    const tick = () => setNowMs(Date.now());
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, [intent?.expires_at, showExpired]);

  const loadIntent = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const res = await fetchCheckoutIntent(token);
    if (res.ok && res.data) {
      setIntent(res.data);
    } else {
      toast.error(res.error?.message ?? 'Payment link not found or expired');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    const label =
      intent?.org_branding?.pay_label
      || (intent?.org === 'labs' ? 'ZeroTail Research Labs' : 'ZeroTail Pay');
    document.title = label;
    return () => {
      document.title = 'ZeroTail Dashboard';
    };
  }, [intent?.org, intent?.org_branding?.pay_label]);

  const orgBranding = intent?.org_branding ?? (intent?.org ? { org: intent.org } : null);

  useEffect(() => {
    void loadIntent();
  }, [loadIntent]);

  // Capture-on-return: PayPal appends the order id as `token` (with `PayerID`)
  // to the success URL. Best-effort; the webhook remains the source of truth.
  const paypalOrderId = searchParams.get('token');
  const paypalPayerId = searchParams.get('PayerID');
  useEffect(() => {
    if (status === 'success' && token && paypalOrderId && paypalPayerId) {
      void capturePaypalCheckout(token, paypalOrderId);
    }
  }, [status, token, paypalOrderId, paypalPayerId]);

  const configuredMethods = useMemo(
    () => (intent?.methods ?? []).filter((m) => m.configured),
    [intent],
  );

  const hasCartBreakdown = Boolean(intent?.line_items && intent.line_items.length > 0);

  const formatMoney = (value: string | null | undefined) => {
    if (!value) return '0.00';
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(2) : value;
  };

  const moneroInProgress = Boolean(
    intent?.payment_status === 'processing' && intent.monero_address && intent.monero_amount_xmr,
  );

  const handlePay = async (method: CheckoutMethod) => {
    if (!token || showExpired) return;

    if (method.id === 'monero') {
      navigate(`/p/${token}/monero`);
      return;
    }

    setPaying(method.id);
    let res = await payCheckoutIntent(token, method.id);
    if (
      !res.ok &&
      res.error?.message?.toLowerCase().includes('processing')
    ) {
      const resetRes = await resetCheckoutIntent(token);
      if (resetRes.ok && resetRes.data) {
        setIntent(resetRes.data);
        res = await payCheckoutIntent(token, method.id);
      }
    }
    setPaying(null);

    if (!res.ok || !res.data) {
      // Intent is gone/consumed (paid, expired, cancelled, failed) or not
      // found — reload so the UI flips to the terminal state instead of
      // repeatedly firing pay/{method} requests that 410.
      if (res.error?.status === 410 || res.error?.status === 404) {
        await loadIntent();
        return;
      }
      toast.error(res.error?.message ?? 'Payment could not be started');
      return;
    }

    const data = res.data as Record<string, unknown>;
    const redirect =
      (data.gateway_url as string | undefined) ??
      (data.checkout_url as string | undefined) ??
      (data.payment_url as string | undefined) ??
      (data.approval_url as string | undefined);

    if (data.status === 'not_configured') {
      toast.error(`${method.label} is not configured yet`);
      return;
    }

    if (redirect) {
      window.location.href = redirect;
      return;
    }

    if (data.status === 'ok' && data.token) {
      window.location.href = `https://app.paymento.io/gateway?token=${String(data.token)}`;
      return;
    }

    toast.error(
      (data.error as string | undefined) ??
        res.error?.message ??
        'No payment URL returned',
    );
  };

  if (status === 'success') {
    return (
      <CheckoutShell branding={orgBranding}>
        <StatusMessage
          icon={
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-zt-primary/15 text-xl text-zt-primary ring-1 ring-zt-primary/30">
              ✓
            </div>
          }
          title="Payment submitted"
          description="Thank you. On-chain payments may take a few minutes to confirm."
        />
      </CheckoutShell>
    );
  }

  if (status === 'cancelled') {
    return (
      <CheckoutShell branding={orgBranding}>
        <StatusMessage
          title="Payment cancelled"
          description="No charge was made. You can return to your payment link when ready."
        />
      </CheckoutShell>
    );
  }

  if (!token) {
    return (
      <CheckoutShell branding={orgBranding}>
        <StatusMessage
          icon={
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl border border-zt-border bg-zt-elevated text-zt-primary">
              <Link2 className="h-5 w-5" aria-hidden="true" />
            </div>
          }
          title="Payment link required"
          description="Open this page using the payment link you were sent. Contact whoever sent you the link if you need help."
        />
      </CheckoutShell>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zt-bg text-zt-muted">
        <Loader2 className="mr-2 h-6 w-6 animate-spin text-zt-primary" />
        Loading payment…
      </div>
    );
  }

  if (!intent) {
    return (
      <CheckoutShell branding={orgBranding}>
        <StatusMessage
          title="Payment not found"
          description="This link is invalid or has expired. Request a new one from your invoice."
        />
      </CheckoutShell>
    );
  }

  const terminalStatus = showExpired
    ? 'expired'
    : TERMINAL_STATUS_COPY[intent.payment_status]
      ? intent.payment_status
      : null;

  if (terminalStatus) {
    const copy = TERMINAL_STATUS_COPY[terminalStatus];
    return (
      <CheckoutShell branding={orgBranding}>
        <StatusMessage title={copy.title} description={copy.description} />
      </CheckoutShell>
    );
  }

  return (
    <CheckoutShell compact branding={orgBranding}>
      {moneroInProgress ? (
        <div className="mb-4 rounded-xl border border-zt-primary/30 bg-zt-primary/10 px-4 py-3 text-sm text-slate-200">
          <p className="font-medium">Monero payment in progress</p>
          <p className="mt-1 text-zt-muted">
            Waiting for your XMR transfer. You can continue on the Monero page or choose another method below.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/p/${token}/monero`)}
            className="mt-2 text-sm font-medium text-zt-primary transition hover:text-zt-accent"
          >
            Continue Monero payment
          </button>
        </div>
      ) : null}
      <div className="mb-6 text-center">
        <h1 className="text-xl font-semibold tracking-tight text-slate-100">Complete payment</h1>
        <p className="mt-1 text-sm text-zt-muted">Choose your preferred method below</p>
      </div>

      <div className="rounded-xl border border-zt-border bg-zt-elevated px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-zt-muted">Amount due</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-100">
          {intent.currency} {intent.amount}
        </p>
        {intent.description && (
          <p className="mt-1 text-sm text-zt-muted">{intent.description}</p>
        )}
        {intent.invoice_ref && (
          <p className="mt-1 text-xs text-zt-muted">Ref: {intent.invoice_ref}</p>
        )}
        {intent.expires_at && (
          <p
            className={`mt-2 text-xs font-medium ${
              expiredByTime ? 'text-zt-warning' : 'text-zt-muted'
            }`}
          >
            {formatExpiryCountdown(intent.expires_at, nowMs)}
          </p>
        )}
      </div>

      {hasCartBreakdown && (
        <div className="mt-4 overflow-hidden rounded-xl border border-zt-border bg-zt-elevated">
          <div className="border-b border-zt-border px-4 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-zt-muted">Order summary</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[280px] text-left text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-zt-muted">
                <tr>
                  <th className="px-4 py-2 font-semibold">Item</th>
                  <th className="hidden px-2 py-2 font-semibold sm:table-cell">SKU</th>
                  <th className="px-2 py-2 text-right font-semibold">Qty</th>
                  <th className="px-4 py-2 text-right font-semibold">Price</th>
                </tr>
              </thead>
              <tbody>
                {intent.line_items?.map((item) => {
                  const lineTotal = (
                    Number(item.unit_price) * (item.quantity || 1)
                  ).toFixed(2);
                  return (
                    <tr key={item.id ?? `${item.sku}-${item.name}`} className="border-t border-zt-border/60">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-100">{item.name}</p>
                        {item.description && (
                          <p className="mt-0.5 text-xs text-zt-muted">{item.description}</p>
                        )}
                        {item.sku && (
                          <p className="mt-0.5 text-xs text-zt-muted sm:hidden">SKU: {item.sku}</p>
                        )}
                      </td>
                      <td className="hidden px-2 py-3 text-zt-muted sm:table-cell">{item.sku ?? '—'}</td>
                      <td className="px-2 py-3 text-right tabular-nums text-slate-200">{item.quantity}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-200">
                        {intent.currency} {lineTotal}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="space-y-1 border-t border-zt-border px-4 py-3 text-sm">
            {intent.subtotal && (
              <div className="flex justify-between text-zt-muted">
                <span>Subtotal</span>
                <span className="tabular-nums text-slate-200">
                  {intent.currency} {formatMoney(intent.subtotal)}
                </span>
              </div>
            )}
            {intent.discount_total && Number(intent.discount_total) > 0 && (
              <div className="flex justify-between text-zt-muted">
                <span>Discount{intent.discount_code ? ` (${intent.discount_code})` : ''}</span>
                <span className="tabular-nums text-emerald-400">
                  −{intent.currency} {formatMoney(intent.discount_total)}
                </span>
              </div>
            )}
            {intent.shipping_total && Number(intent.shipping_total) > 0 && (
              <div className="flex justify-between text-zt-muted">
                <span>Shipping</span>
                <span className="tabular-nums text-slate-200">
                  {intent.currency} {formatMoney(intent.shipping_total)}
                </span>
              </div>
            )}
            {intent.tax_total && Number(intent.tax_total) > 0 && (
              <div className="flex justify-between text-zt-muted">
                <span>Tax</span>
                <span className="tabular-nums text-slate-200">
                  {intent.currency} {formatMoney(intent.tax_total)}
                </span>
              </div>
            )}
            <div className="flex justify-between border-t border-zt-border/60 pt-2 font-medium text-slate-100">
              <span>Total</span>
              <span className="tabular-nums">
                {intent.currency} {formatMoney(intent.amount)}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-zt-muted">
          Payment method
        </p>

        {configuredMethods.length === 0 ? (
          <p className="rounded-lg border border-dashed border-zt-border px-4 py-6 text-center text-sm text-zt-muted">
            No payment providers are configured yet.
          </p>
        ) : (
          <div className="max-h-[min(28rem,calc(100dvh-18rem))] space-y-2 overflow-y-auto overflow-x-hidden overscroll-y-contain pr-0.5">
            {(intent.methods ?? []).map((method) => {
              const disabled = !method.configured || paying !== null || showExpired;
              return (
                <button
                  key={method.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => void handlePay(method)}
                  className={`grid w-full grid-cols-[2.5rem_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 rounded-xl border px-3 py-3 text-left transition sm:px-4 ${
                    method.configured
                      ? 'border-zt-border bg-zt-bg hover:border-zt-primary/50 hover:bg-zt-elevated/60'
                      : 'cursor-not-allowed border-zt-border/50 bg-zt-bg/50 opacity-50'
                  }`}
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-zt-border bg-zt-surface text-zt-primary">
                    {paying === method.id ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <MethodIcon methodId={method.id} className="h-5 w-5" />
                    )}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-medium text-slate-100">{METHOD_DISPLAY[method.id]?.label ?? method.label}</span>
                    {method.description ? (
                      <span className="block text-xs leading-snug text-zt-muted">{METHOD_DISPLAY[method.id]?.description ?? method.description}</span>
                    ) : null}
                  </span>
                  {!method.configured && (
                    <span
                      className="shrink-0 whitespace-nowrap rounded-md border border-zt-warning/30 bg-zt-warning/10 px-2 py-0.5 text-[11px] font-medium text-zt-warning"
                      title="Setup needed"
                    >
                      Setup
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </CheckoutShell>
  );
}
